/**
 * <GratitudeTimeline /> — weekly/monthly gratitude rhythm ribbon.
 *
 * Variants:
 *   • "wall"      — full interactive timeline (default). Click-to-expand
 *                   reveals card previews for the clicked bucket.
 *   • "dashboard" — compact sparkline. No expansion; click navigates to
 *                   /tools/gratitude. Smaller dots, no per-column labels.
 *   • "profile"   — same visuals as wall but read-only (no edit actions on
 *                   expansion rows) and mount animation is viewport-triggered.
 *
 * Dots are colored:
 *   rose   = offered / given
 *   violet = received
 *   area-colored = self-gratitude (from wheelAreaMap[wheel_area_key])
 *
 * A11y: each bucket is a focusable <button> with aria-label; ribbon supports
 * Left/Right arrow keys to move focus, Enter to toggle, Esc to close.
 * Respects prefers-reduced-motion (disables stagger + hover lift).
 */

import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useReducedMotion, useInView } from "framer-motion";
import { Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const COLOR_GIVEN = "#E3638E"; // rose
const COLOR_RECEIVED = "#7C5FD4"; // brand violet
const COLOR_SELF_FALLBACK = "#94A3B8";

const MAX_DOTS_VISIBLE = 10;
const DEFAULT_LOOKBACK_DAYS = 30;
const DEFAULT_LOOKBACK_WEEKS = 12;
const DEFAULT_LOOKBACK_MONTHS = 6;
const CAP_LOOKBACK_DAYS = 60;
const CAP_LOOKBACK_WEEKS = 26;
const CAP_LOOKBACK_MONTHS = 12;

// ---------- date helpers ----------
function dayStart(date) {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}
function weekStart(date) {
  const d = new Date(date);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  d.setUTCHours(0, 0, 0, 0);
  return d;
}
function monthStart(date) {
  const d = new Date(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function bucketStarter(bucket) {
  if (bucket === "day") return dayStart;
  if (bucket === "month") return monthStart;
  return weekStart;
}
function stepBucket(date, bucket, delta) {
  const d = new Date(date);
  if (bucket === "day") d.setUTCDate(d.getUTCDate() + delta);
  else if (bucket === "week") d.setUTCDate(d.getUTCDate() + delta * 7);
  else d.setUTCMonth(d.getUTCMonth() + delta);
  return d;
}
function formatBucketLabel(date, bucket) {
  if (bucket === "day") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  if (bucket === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}
// Build a grid of bucket-start Dates from `startDate` through today (inclusive).
// If startDate is null/undefined, falls back to the default lookback count.
function buildGrid(bucket, defaultCount, capCount, startDate) {
  const starter = bucketStarter(bucket);
  const now = starter(new Date());
  // Decide how far back we go.
  // - If we have a startDate (user join), start from that bucket exactly
  //   (no empty pre-join buckets) — bounded by capCount.
  // - Otherwise use the default count.
  let count = defaultCount;
  if (startDate instanceof Date && !Number.isNaN(startDate.getTime())) {
    const startBucket = starter(startDate);
    let n = 1;
    let cursor = new Date(now);
    // Count buckets from now back to startBucket (inclusive)
    while (cursor.getTime() > startBucket.getTime() && n < capCount) {
      cursor = stepBucket(cursor, bucket, -1);
      n += 1;
    }
    count = Math.min(n, capCount);
  }
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    out.push(stepBucket(now, bucket, -i));
  }
  return out;
}

// Size tokens per variant
const VARIANT_TOKENS = {
  wall: {
    colMinWidth: 30,
    dotSize: 7,
    capstoneSize: 9,
    stackMinHeight: 64,
    showLabels: true,
    showExpansion: true,
    showHeader: true,
    padding: 16,
    showBaseline: true,
    showPulse: true,
    showBreathing: true,
    showCapstoneRing: true,
    showInkDrop: true,
    showQuietWeekCopy: true,
  },
  profile: {
    colMinWidth: 32,
    dotSize: 7,
    capstoneSize: 9,
    stackMinHeight: 64,
    showLabels: true,
    showExpansion: false,
    showHeader: true,
    padding: 16,
    showBaseline: true,
    showPulse: false,
    showBreathing: false,
    showCapstoneRing: true,
    showInkDrop: true,
    showQuietWeekCopy: false,
  },
  dashboard: {
    colMinWidth: 20,
    dotSize: 5,
    capstoneSize: 6,
    stackMinHeight: 44,
    showLabels: false,
    showExpansion: false,
    showHeader: true,
    padding: 12,
    showBaseline: false,
    showPulse: false,
    showBreathing: false,
    showCapstoneRing: false,
    showInkDrop: false,
    showQuietWeekCopy: false,
  },
};

// ---------- component ----------
export default function GratitudeTimeline({
  cards = [],
  wheelAreaMap = {},
  defaultBucket = "day",
  variant = "wall",
  ownerFirstName = null,
  // join milestone — when provided, ribbon starts at the join bucket
  // (up to per-bucket caps) and renders a special avatar "milestone" column
  joinedAt = null,
  joinedPicture = null,
  // dashboard: route to on click
  navigateOnClick = null,
}) {
  const { t } = useTranslation();
  // localStorage key scoped by variant so wall / dashboard / profile each
  // remember their own last-chosen bucket. Profile scope is shared across
  // all profiles (viewer preference, not per-wall).
  const storageKey = `gratitude-timeline-bucket:${variant}`;
  const [bucket, setBucketState] = useState(() => {
    if (typeof window === "undefined") return defaultBucket;
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved === "day" || saved === "week" || saved === "month") return saved;
    } catch {
      /* ignore quota / privacy-mode errors */
    }
    return defaultBucket;
  });
  const setBucket = (next) => {
    setBucketState(next);
    try {
      window.localStorage.setItem(storageKey, next);
    } catch {
      /* ignore */
    }
  };
  const [expandedKey, setExpandedKey] = useState(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const ribbonRef = useRef(null);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.2 });

  const tokens = VARIANT_TOKENS[variant] || VARIANT_TOKENS.wall;
  const { defaultCount, capCount, bucketUnitLabel } =
    bucket === "day"
      ? { defaultCount: DEFAULT_LOOKBACK_DAYS, capCount: CAP_LOOKBACK_DAYS, bucketUnitLabel: "days" }
      : bucket === "month"
      ? { defaultCount: DEFAULT_LOOKBACK_MONTHS, capCount: CAP_LOOKBACK_MONTHS, bucketUnitLabel: "months" }
      : { defaultCount: DEFAULT_LOOKBACK_WEEKS, capCount: CAP_LOOKBACK_WEEKS, bucketUnitLabel: "weeks" };

  const joinDate = useMemo(() => {
    if (!joinedAt) return null;
    const d = new Date(joinedAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [joinedAt]);

  const { grid, byBucket, max, joinBucketKey } = useMemo(() => {
    const starter = bucketStarter(bucket);
    const gridDates = buildGrid(bucket, defaultCount, capCount, joinDate);
    const map = new Map();
    gridDates.forEach((d) =>
      map.set(d.getTime(), { date: d, given: [], received: [], self: [] })
    );

    cards.forEach((c) => {
      if (!c?.created_at || c.deleted) return;
      if (c.perspective === "received" && c.target_visibility === "removed")
        return;
      const created = new Date(c.created_at);
      if (Number.isNaN(created.getTime())) return;
      const key = starter(created).getTime();
      if (!map.has(key)) return;
      const entry = map.get(key);
      // Multi-area expansion: a card tagged with N life-areas should render
      // N dots in the rhythm column — one per area, for *any* perspective
      // (self / given / received). This matches the card's footer chip
      // strip and the Gratitude Pulse counters, which count each card once
      // per linked area. Cards with no areas still render exactly one dot.
      const rawAreas = Array.isArray(c.wheel_area_keys) && c.wheel_area_keys.length
        ? c.wheel_area_keys
        : (c.wheel_area_key ? [c.wheel_area_key] : [null]);
      const bucketFor = {
        mine: entry.self,
        given: entry.given,
        received: entry.received,
      }[c.perspective];
      if (!bucketFor) return;
      rawAreas.forEach((k) => {
        bucketFor.push({ ...c, _area_key: k });
      });
    });

    let highest = 0;
    map.forEach((e) => {
      const t = e.given.length + e.received.length + e.self.length;
      if (t > highest) highest = t;
    });

    // Where does the join milestone sit? Only render it if the join date
    // falls inside the displayed grid.
    let joinKey = null;
    if (joinDate) {
      const jk = starter(joinDate).getTime();
      if (map.has(jk)) joinKey = jk;
    }

    return { grid: gridDates, byBucket: map, max: highest, joinBucketKey: joinKey };
  }, [cards, bucket, defaultCount, capCount, joinDate]);

  const isEmpty = max === 0;

  // First-entry celebration — fires once per bucket per user per browser
  // when the current bucket transitions from 0 cards to 1+ cards.
  // • Wall variant only (celebrating your own activity, not someone else's).
  // • Daily bucket only — weekly/monthly make the signal too noisy
  //   (re-firing on a new week is an every-7-days moment, too frequent for
  //   the "quiet-stretch-broken" emotional beat we want; new-month is too
  //   rare to test / debug). Daily is the sweet spot.
  // • Idempotent via localStorage.
  const lastKeyRef = useRef(null);
  const prevTotalRef = useRef(null);
  useEffect(() => {
    if (variant !== "wall") return;
    if (bucket !== "day") {
      prevTotalRef.current = null;
      lastKeyRef.current = null;
      return;
    }
    if (!grid.length) return;
    const lastKey = grid[grid.length - 1].getTime();
    const entry = byBucket.get(lastKey);
    const curTotal = entry ? entry.given.length + entry.received.length + entry.self.length : 0;
    const prev = prevTotalRef.current;
    const prevKey = lastKeyRef.current;
    // Only celebrate on a real same-bucket transition from 0 → ≥1.
    // The first pass (when prev is null) or a bucket rollover doesn't count.
    if (prev === 0 && curTotal > 0 && prevKey === lastKey) {
      const storageKey = `gratitude-timeline-celebrated:day:${lastKey}`;
      let already = false;
      try {
        already = !!window.localStorage.getItem(storageKey);
      } catch { /* ignore */ }
      if (!already) {
        try { window.localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
        toast.success("Your first note today ✨", {
          description: "The wall notices.",
          duration: 3500,
        });
        if (!reducedMotion) {
          // Confetti from the last bucket column's position. rAF so the
          // DOM has the new column in place.
          requestAnimationFrame(() => {
            const rib = ribbonRef.current;
            const last = rib?.querySelector(`[data-bucket-idx="${grid.length - 1}"]`);
            const rect = last?.getBoundingClientRect();
            const x = rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5;
            const y = rect ? Math.max(0.1, (rect.top - 10) / window.innerHeight) : 0.35;
            try {
              confetti({
                particleCount: 32,
                spread: 55,
                startVelocity: 32,
                gravity: 0.9,
                ticks: 90,
                scalar: 0.9,
                origin: { x, y },
                colors: ["#7C5FD4", "#E3638E", "#94A3B8", "#f4c95d"],
                disableForReducedMotion: true,
              });
            } catch { /* confetti errors are non-fatal */ }
          });
        }
      }
    }
    prevTotalRef.current = curTotal;
    lastKeyRef.current = lastKey;
  }, [cards, bucket, variant, grid, byBucket, reducedMotion]);

  // Center the current (rightmost) bucket in the ribbon viewport whenever
  // the bucket changes (or on initial mount). Uses scrollIntoView with
  // `inline:"center"` so the "today" column is always framed in view even
  // after re-bucket. Must be declared BEFORE any conditional return to
  // respect React Hook rules-of-hooks.
  useEffect(() => {
    if (isEmpty && !joinBucketKey) return undefined;
    const rib = ribbonRef.current;
    if (!rib) return undefined;
    const last = rib.querySelector(`[data-bucket-idx="${grid.length - 1}"]`);
    if (!last) return undefined;
    const id = requestAnimationFrame(() => {
      try {
        last.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", inline: "center", block: "nearest" });
      } catch {
        rib.scrollLeft = last.offsetLeft - rib.clientWidth / 2 + last.clientWidth / 2;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [bucket, grid.length, reducedMotion, isEmpty, joinBucketKey]);

  if (isEmpty && !joinBucketKey) return null;

  const expandedEntry = expandedKey ? byBucket.get(expandedKey) : null;

  // Keyboard nav across buckets (skip disabled empty ones)
  const onRibbonKeyDown = (e) => {
    if (e.key === "Escape" && expandedKey) {
      e.preventDefault();
      setExpandedKey(null);
      return;
    }
    if (!["ArrowLeft", "ArrowRight"].includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const start = focusIdx < 0 ? grid.length - 1 : focusIdx;
    let next = start + dir;
    // Walk past empty (disabled) buckets; stop if we fall off either edge.
    while (next >= 0 && next < grid.length) {
      const d = grid[next];
      const entry = byBucket.get(d.getTime());
      const total = entry.given.length + entry.received.length + entry.self.length;
      if (total > 0) break;
      next += dir;
    }
    if (next < 0 || next >= grid.length) return; // nothing focusable that way
    setFocusIdx(next);
    const btn = ribbonRef.current?.querySelector(`[data-bucket-idx="${next}"]`);
    btn?.focus();
  };

  const handleBucketClick = (d) => {
    if (variant === "dashboard") {
      if (navigateOnClick) navigateOnClick();
      else navigate("/tools/gratitude");
      return;
    }
    if (!tokens.showExpansion) return;
    const key = d.getTime();
    setExpandedKey(expandedKey === key ? null : key);
  };

  const staggerDelay = reducedMotion ? 0 : 0.03;
  const shouldAnimate = variant !== "profile" || inView;

  // Header subtitle removed — the title + bucket toggle already tell
  // the story; the prompt was crowding the mobile view.
  const subtitle = null;

  return (
    <section
      ref={sectionRef}
      className="rounded-xl"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-default)",
        padding: tokens.padding,
        marginBottom: variant === "dashboard" ? 16 : 32,
      }}
      data-testid={`gratitude-timeline-${variant}`}
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3
            className="font-semibold text-sm flex items-center gap-2"
            style={{ color: "var(--color-text-primary)" }}
          >
            <Clock size={14} style={{ color: "var(--color-brand-primary)" }} />
            {variant === "profile" && ownerFirstName
              ? t("gratitude.rhythmFor", { name: ownerFirstName, defaultValue: `${ownerFirstName}'s gratitude rhythm` })
              : t("gratitude.rhythm")}
          </h3>
          {subtitle && (
            <p
              className="text-xs mt-0.5"
              style={{ color: "var(--color-text-secondary)" }}
            >
              {subtitle}
            </p>
          )}
        </div>
        <div
          className="inline-flex rounded-full p-0.5 text-xs font-medium"
          style={{ background: "var(--color-surface-subtle)" }}
          data-testid="timeline-bucket-toggle"
        >
          {["day", "week", "month"].map((b) => (
            <button
              key={b}
              onClick={(e) => {
                e.stopPropagation();
                setBucket(b);
                setExpandedKey(null);
              }}
              className="px-3 py-1 rounded-full transition-colors"
              style={{
                background:
                  bucket === b ? "var(--color-brand-primary)" : "transparent",
                color:
                  bucket === b
                    ? "var(--color-text-on-brand)"
                    : "var(--color-text-secondary)",
              }}
              data-testid={`timeline-bucket-${b}`}
            >
              {b === "day" ? t("gratitude.periodDaily") : b === "week" ? t("gratitude.periodWeekly") : t("gratitude.periodMonthly")}
            </button>
          ))}
        </div>
      </header>

      {/* Ribbon — the 50% inline-padding lets any column (including "today")
          scroll to the visual center of the container, regardless of how few
          buckets there are. Works with scroll-snap + scrollIntoView. */}
      <div
        ref={ribbonRef}
        role="toolbar"
        aria-label="Gratitude timeline"
        onKeyDown={onRibbonKeyDown}
        className="relative flex gap-1 items-end overflow-x-auto pb-2 outline-none"
        style={{
          scrollSnapType: "x proximity",
          paddingLeft: "50%",
          paddingRight: "50%",
          scrollPaddingLeft: "50%",
          scrollPaddingRight: "50%",
        }}
      >
        {/* Baseline gradient — a thin horizontal line at the foot of the
            dot-stack that fades from ghost → brand at the right edge.
            Gives the ribbon a visible "ground" so empty weeks read as
            "quiet" rather than "missing data." */}
        {tokens.showBaseline && (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 right-0"
            style={{
              bottom: tokens.showLabels ? 22 : 8,
              height: 1,
              background:
                "linear-gradient(to right, var(--color-brand-ghost) 0%, var(--color-brand-ghost) 65%, var(--color-brand-primary) 100%)",
              opacity: 0.45,
              transformOrigin: "left center",
              zIndex: 0,
            }}
            initial={reducedMotion ? { opacity: 0.45 } : { scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 0.45 }}
            transition={{ duration: reducedMotion ? 0.1 : 0.6, ease: "easeOut" }}
          />
        )}

        {/* framer-motion's `layout` prop on each BucketColumn gives
            smooth repositioning when the grid count changes (bucket toggle):
            surviving-by-key columns slide to their new positions; exiting
            columns fade+shrink toward the center; entering columns fade in.
            Not wrapping in <LayoutGroup> — combining it with popLayout was
            leaking old DOM nodes during toggles. */}
        <AnimatePresence mode="popLayout">
          {grid.map((d, i) => {
            const entry = byBucket.get(d.getTime());
            const total =
              entry.given.length + entry.received.length + entry.self.length;
            const isExpanded = expandedKey === d.getTime();
            const isLast = i === grid.length - 1;
            const isJoin = joinBucketKey !== null && d.getTime() === joinBucketKey;
            return (
              <BucketColumn
                key={`${bucket}-${d.getTime()}`}
                date={d}
                entry={entry}
                total={total}
                bucket={bucket}
                wheelAreaMap={wheelAreaMap}
                isExpanded={isExpanded}
                anyExpanded={!!expandedKey}
                delay={shouldAnimate ? i * staggerDelay : 0}
                tokens={tokens}
                reducedMotion={reducedMotion}
                onClick={() => handleBucketClick(d)}
                idx={i}
                isLast={isLast}
                isJoin={isJoin}
                joinedPicture={joinedPicture}
                ownerFirstName={ownerFirstName}
                variant={variant}
              />
            );
          })}
        </AnimatePresence>
      </div>

      {/* Quiet-week compassionate copy — only when past activity exists
          but this week is still empty. Never shown on fresh/empty walls. */}
      {tokens.showQuietWeekCopy && (() => {
        const lastKey = grid[grid.length - 1].getTime();
        const cur = byBucket.get(lastKey);
        const curTotal = cur.given.length + cur.received.length + cur.self.length;
        if (curTotal > 0) return null;
        return (
          <motion.p
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.6, duration: 0.4 }}
            className="text-center text-sm italic mt-2 mb-1"
            style={{
              color: "var(--color-text-secondary)",
              fontSize: 14,
              letterSpacing: "0.01em",
            }}
            data-testid="timeline-quiet-week"
          >
            Quiet {bucket === "day" ? "day" : bucket === "week" ? "week" : "month"}. Who are you thinking of?
          </motion.p>
        );
      })()}

      {/* Legend */}
      <div
        className="flex items-center gap-4 mt-2 text-[10px] flex-wrap"
        style={{ color: "var(--color-text-secondary)" }}
      >
        <LegendDot color={COLOR_RECEIVED} label={t("gratitude.legendReceived")} />
        <LegendDot color={COLOR_GIVEN} label={t("gratitude.legendGiven")} />
        <LegendDot color={COLOR_SELF_FALLBACK} label={t("gratitude.legendSelf")} />
      </div>

      {/* Expansion (wall + profile only) */}
      <AnimatePresence initial={false}>
        {expandedEntry && tokens.showExpansion && (
          <motion.div
            key="expanded"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, height: "auto", marginTop: 16 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: reducedMotion ? 0.1 : 0.26, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
            data-testid="timeline-expansion"
          >
            <ExpansionPanel
              entry={expandedEntry}
              bucket={bucket}
              wheelAreaMap={wheelAreaMap}
              onClose={() => setExpandedKey(null)}
              variant={variant}
              ownerFirstName={ownerFirstName}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ---------- bucket column ----------
function BucketColumn({
  date,
  entry,
  total,
  bucket,
  wheelAreaMap,
  isExpanded,
  anyExpanded,
  delay,
  tokens,
  reducedMotion,
  onClick,
  idx,
  isLast,
  isJoin,
  joinedPicture,
  ownerFirstName,
  variant,
}) {
  const selfColorFor = (c) => {
    // Multi-area expansion attaches `_area_key` on each entry. Fall back to
    // the legacy singular field so older data keeps rendering the same colour.
    const k = c._area_key !== undefined ? c._area_key : c.wheel_area_key;
    return (k && wheelAreaMap[k]?.color) || COLOR_SELF_FALLBACK;
  };

  const dots = [
    ...entry.received.map(() => COLOR_RECEIVED),
    ...entry.given.map(() => COLOR_GIVEN),
    ...entry.self.map(selfColorFor),
  ];
  const overflow = Math.max(0, dots.length - MAX_DOTS_VISIBLE);
  const visibleDots = overflow > 0 ? dots.slice(0, MAX_DOTS_VISIBLE) : dots;
  // Capstone = the top-most dot visually. With flex-col-reverse the DOM's
  // last child sits at the top of the column. When overflow is showing,
  // the overflow "+N" chip becomes the visual capstone, not a dot.
  const capstoneDotIdx = overflow > 0 ? -1 : visibleDots.length - 1;

  const label = formatBucketLabel(date, bucket);
  const joinSuffix =
    isJoin
      ? ` · ${variant === "profile" && ownerFirstName ? `${ownerFirstName} joined Evoluras` : "You joined Evoluras"}`
      : "";
  const aria =
    total === 0
      ? `${label}: nothing yet${joinSuffix}`
      : `${label}: ${entry.given.length} given, ${entry.received.length} received, ${entry.self.length} self${joinSuffix}`;

  const testId = `timeline-bucket-${date.toISOString().slice(0, 10)}`;

  // Current-week (isLast) "breathing" — only when the current bucket has
  // activity. Never animate past buckets (breaks the "past is still"
  // mental model) or empty buckets (nothing to celebrate).
  const breathe =
    tokens.showBreathing && isLast && total > 0 && !reducedMotion;

  // Ink-drop per-dot mount delay inside the column: each dot drops in from
  // slightly above, spring-damped, stagger 35ms. Keeps the "ritual" feel
  // without being slow (max column = 10 dots → ≤350ms).
  const inkDrop = tokens.showInkDrop && !reducedMotion;

  return (
    <motion.button
      layout
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={
        breathe
          ? {
              opacity: anyExpanded && !isExpanded ? 0.55 : 1,
              y: 0,
              scale: [1, 1.035, 1],
            }
          : {
              opacity: anyExpanded && !isExpanded ? 0.55 : 1,
              y: 0,
            }
      }
      exit={{ opacity: 0 }}
      whileHover={reducedMotion || total === 0 || !tokens.showExpansion ? {} : { y: -3 }}
      transition={
        breathe
          ? {
              delay,
              duration: 0.28,
              ease: "easeOut",
              scale: {
                duration: 4.2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1.2, // wait for mount to settle
              },
            }
          : { delay, duration: reducedMotion ? 0.2 : 0.28, ease: "easeOut" }
      }
      onClick={total > 0 && tokens.showExpansion ? onClick : undefined}
      disabled={total === 0 || !tokens.showExpansion}
      data-bucket-idx={idx}
      className="relative flex flex-col items-center gap-1 px-0.5 py-1 rounded-lg transition-colors focus:outline-none focus-visible:ring-2"
      style={{
        background: isExpanded ? "var(--color-brand-ghost)" : "transparent",
        cursor: total > 0 && tokens.showExpansion ? "pointer" : "default",
        scrollSnapAlign: "center",
        minWidth: tokens.colMinWidth,
        zIndex: isLast ? 1 : undefined,
      }}
      aria-expanded={isExpanded}
      aria-label={aria}
      title={aria}
      data-testid={testId}
    >
      {/* Current-week pulse glow — sits behind the dot stack. Radial
          gradient in brand violet; only rendered for the live column
          and only when it has activity (no sympathetic pulse for empty).
          Ambient breathing of its opacity adds subtle life to the page. */}
      {tokens.showPulse && isLast && total > 0 && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            left: "50%",
            bottom: tokens.showLabels ? 14 : 0,
            transform: "translateX(-50%)",
            width: 56,
            height: 56,
            borderRadius: "50%",
            background:
              "radial-gradient(closest-side, rgba(83,74,183,0.22), rgba(83,74,183,0) 72%)",
            zIndex: 0,
          }}
          initial={{ opacity: 0 }}
          animate={
            reducedMotion
              ? { opacity: 0.55 }
              : { opacity: [0.35, 0.65, 0.35] }
          }
          transition={
            reducedMotion
              ? { duration: 0.2 }
              : { duration: 4.2, repeat: Infinity, ease: "easeInOut" }
          }
        />
      )}

      {/* Join milestone — avatar ring that sits just above the dot stack
          on the column representing the user's join bucket. Pure flourish,
          aria-hidden. Hover tooltip carries the semantics. */}
      {isJoin && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute block overflow-hidden"
          style={{
            left: "50%",
            top: -6,
            transform: "translateX(-50%)",
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "var(--color-surface)",
            boxShadow:
              "0 0 0 2px var(--color-brand-primary), 0 2px 8px rgba(83,74,183,0.28)",
            zIndex: 2,
          }}
          initial={
            reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.4, y: -4 }
          }
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            delay: delay + 0.15,
            type: "spring",
            stiffness: 220,
            damping: 16,
          }}
        >
          {joinedPicture ? (
            <img
              src={joinedPicture}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
              }}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: "var(--color-brand-ghost)",
                color: "var(--color-brand-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {(ownerFirstName || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
        </motion.span>
      )}

      {/* Dot stack */}
      <div
        className="flex flex-col-reverse items-center gap-[2px] justify-end relative"
        style={{ minHeight: tokens.stackMinHeight, zIndex: 1 }}
        aria-hidden="true"
      >
        {overflow > 0 && (
          <span
            className="text-[8px] font-semibold"
            style={{ color: "var(--color-text-secondary)" }}
          >
            +{overflow}
          </span>
        )}
        {visibleDots.map((color, dIdx) => {
          const isCapstone = tokens.showCapstoneRing && dIdx === capstoneDotIdx;
          const size = isCapstone ? tokens.capstoneSize : tokens.dotSize;
          return (
            <motion.span
              key={dIdx}
              layout
              className="rounded-full"
              initial={
                inkDrop
                  ? { y: -10, opacity: 0, scale: 0.6 }
                  : { opacity: 0 }
              }
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={
                inkDrop
                  ? {
                      delay: delay + 0.25 + dIdx * 0.035,
                      type: "spring",
                      stiffness: 240,
                      damping: 14,
                    }
                  : { delay, duration: 0.3 }
              }
              style={{
                background: color,
                width: size,
                height: size,
                boxShadow: isCapstone
                  ? `0 0 0 2px ${color}33, 0 0 8px ${color}44`
                  : isExpanded
                  ? `0 0 0 2px ${color}22`
                  : "none",
              }}
            />
          );
        })}
        {total === 0 && (
          <span
            className="rounded-full"
            style={{
              width: 5,
              height: 5,
              background: "var(--color-border-default)",
            }}
          />
        )}
      </div>
      {/* Label (wall + profile only). Daily view thins labels to every 5th
          day to avoid overlap; always keep the last, join, and expanded.
          An invisible spacer keeps column heights consistent so the
          baseline gradient stays at a single Y across all columns. */}
      {tokens.showLabels && (
        (bucket !== "day" || idx % 5 === 0 || isLast || isJoin || isExpanded) ? (
          <span
            className="text-[10px] whitespace-nowrap"
            style={{
              color: isExpanded
                ? "var(--color-brand-primary)"
                : "var(--color-text-secondary)",
              fontWeight: isExpanded ? 600 : 400,
            }}
          >
            {label}
          </span>
        ) : (
          <span className="text-[10px]" aria-hidden="true" style={{ visibility: "hidden" }}>·</span>
        )
      )}
    </motion.button>
  );
}

// ---------- legend dot ----------
function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="rounded-full"
        style={{ width: 8, height: 8, background: color }}
      />
      {label}
    </span>
  );
}

// ---------- expansion ----------
function ExpansionPanel({ entry, bucket, wheelAreaMap, onClose, variant, ownerFirstName }) {
  const label = formatBucketLabel(entry.date, bucket);
  const totals = {
    given: entry.given.length,
    received: entry.received.length,
    self: entry.self.length,
  };
  // Profile variant renames "Self-gratitude" → "{owner}'s self-reflection"
  const selfLabel =
    variant === "profile" && ownerFirstName
      ? `${ownerFirstName}'s self-reflection`
      : "Self-gratitude";
  const groups = [
    { key: "received", label: t("gratitude.legendReceived"), color: COLOR_RECEIVED, items: entry.received },
    { key: "given", label: t("gratitude.legendGiven"), color: COLOR_GIVEN, items: entry.given },
    { key: "self", label: selfLabel, color: COLOR_SELF_FALLBACK, items: entry.self },
  ].filter((g) => g.items.length > 0);

  return (
    <div
      className="rounded-lg p-4"
      style={{
        background: "var(--color-surface-subtle)",
        border: "1px dashed var(--color-border-active)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} style={{ color: "var(--color-brand-primary)" }} />
          <h4
            className="text-sm font-semibold"
            style={{ color: "var(--color-text-primary)" }}
          >
            {bucket === "week" ? `Week of ${label}` : label}
          </h4>
          <span
            className="text-xs"
            style={{ color: "var(--color-text-secondary)" }}
          >
            · {totals.given} given · {totals.received} received · {totals.self} self
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            color: "var(--color-text-secondary)",
            background: "var(--color-surface)",
          }}
          data-testid="timeline-expansion-close"
        >
          Close
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {groups.map((g) => (
          <div key={g.key}>
            <div
              className="text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"
              style={{ color: "var(--color-text-secondary)" }}
            >
              <span
                className="rounded-full"
                style={{ width: 7, height: 7, background: g.color }}
              />
              {g.label} · {g.items.length}
            </div>
            <ul className="space-y-1.5">
              {g.items.slice(0, 6).map((c) => (
                <CardPreviewRow
                  key={c.card_id}
                  card={c}
                  wheelAreaMap={wheelAreaMap}
                  group={g.key}
                  variant={variant}
                />
              ))}
              {g.items.length > 6 && (
                <li
                  className="text-[11px] italic"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  + {g.items.length - 6} more
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardPreviewRow({ card, wheelAreaMap, group, variant }) {
  // Profile variant only shows first-names (already sanitized backend-side)
  const person =
    group === "given"
      ? card.target_name
      : group === "received"
      ? card.author_name
      : null;
  const area =
    group === "self"
      ? wheelAreaMap[card._area_key !== undefined ? card._area_key : card.wheel_area_key]
      : null;
  const snippet = (card.content || "").slice(0, 90);
  const preposition =
    group === "received" ? "from" : group === "given" ? "to" : null;
  return (
    <li
      className="text-xs p-2 rounded-md"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-default)",
      }}
    >
      <div
        className="flex items-center gap-1.5 mb-0.5 text-[10px]"
        style={{ color: "var(--color-text-secondary)" }}
      >
        {person && <span className="truncate">{preposition} {person}</span>}
        {area && (
          <span className="inline-flex items-center gap-1">
            <span
              className="rounded-full"
              style={{ width: 6, height: 6, background: area.color }}
            />
            {area.label}
          </span>
        )}
      </div>
      <div
        className="leading-snug"
        style={{ color: "var(--color-text-primary)" }}
      >
        {snippet}
        {(card.content || "").length > 90 ? "…" : ""}
      </div>
    </li>
  );
}
